import { t } from "i18next";
import { Control, FieldPath, FieldValues } from "react-hook-form";

import { Combobox } from "src/core/components/custom-ui/combobox";
import { Spinner } from "src/core/components/custom-ui/spinner";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/core/components/ui/form";
import { ISelectOption } from "src/core/constants/options";
import { cn } from "src/core/lib/utils";

type FormComboboxProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  options: ISelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  required?: boolean;
  softRequired?: boolean;
  className?: string;
  control?: Control<T>;
  isLoading?: boolean;
  withBadge?: boolean;
  disabled?: boolean;
  isInModal?: boolean;
};

/**
 *
 * @param softRequired - (boolean) Permet de gérer un affichage de type "warning" (orange)
 * pour les champs qui sont nécessaires à la validation d'un dossier.
 * @param isInModal - (boolean) Permet de rendre fonctionnel le scroll à la souris
 * dans le cas d'une liste déroulante dans une modale.
 */
export function FormCombobox<T extends FieldValues>({
  name,
  label,
  options,
  placeholder = "Sélectionner",
  searchPlaceholder = "Rechercher...",
  emptyMessage = "Aucun résultat trouvé.",
  required = false,
  softRequired = false,
  className = "w-[360px]",
  control,
  isLoading = false,
  withBadge = false,
  disabled = false,
  isInModal = false,
}: FormComboboxProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const isInvalid = fieldState.invalid;
        const showSoftRequired = softRequired && !isInvalid;

        return (
          <FormItem>
            <FormLabel required={required} className={cn(isInvalid && "text-alert")}>
              {label}
            </FormLabel>
            {isLoading ? (
              <div className={`h-[40px] flex items-center ${className}`}>
                <Spinner />
              </div>
            ) : (
              <>
                <FormControl>
                  <div className={className}>
                    <Combobox
                      id={name}
                      list={options}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={placeholder}
                      searchPlaceholder={searchPlaceholder}
                      emptyMessage={emptyMessage}
                      className={cn(
                        className,
                        fieldState.invalid && "border-alert",
                        showSoftRequired && "border-2 border-dashed border-orange-medium"
                      )}
                      withBadge={withBadge}
                      disabled={disabled}
                      isInModal={isInModal}
                    />
                  </div>
                </FormControl>
                <FormMessage />
                {showSoftRequired && (
                  <p className="font-light text-sm text-muted">
                    {t("global.form.warning.softRequired")}
                  </p>
                )}
              </>
            )}
          </FormItem>
        );
      }}
    />
  );
}
